// The send button and its "how to message them" menu (Step 2 item 2; Liam 30 Sep 2026: "what if we right click
// the send icon and then you could switch it? Not sure if that would transfer to phone"). The words and links are
// src/contact.ts; this draws them.
//
// One control everywhere a customer is messaged: the send link, named for the way it sends ("Send as a text"),
// and a small ▾ beside it that opens the menu. On a computer a right-click on the send link opens the same menu;
// on a phone the ▾ is the way in, because a long press on a link belongs to the phone (it previews the link).
// Picking a way saves it on that customer straight away and the caller redraws.

import { callingCode } from './round-config'
import { bindEmailSend } from './email-send'
import { mutate, withBase } from './api'
import { MESSAGE_BY_OPTIONS, isSendPress, linkFor, roundMessageBy, wayFor, wayWords, waysThatWork, type CustomerMessageBy, type Reachable, type SendLink } from './contact'
import { onAPhone, setWhatsappPlace, whatsappPlace, whatsappTarget, type WhatsAppPlace } from './rebook-message'
import { offerUndo, undoPatch } from './undo'
import { toast } from './ui'
import { el } from './ui'

export interface WayCustomer extends Reachable {
  id: number
  name: string
  version?: number
  message_by?: string | null
  // Where the way is saved: a customer (the default) or an enquiry (leads.message_by, 0075; no "Don't message").
  route?: 'customers' | 'leads'
}

// Small marks for each way, drawn like the WhatsApp one on Messages (currentColor, 20px).
export function wayIcon(way: CustomerMessageBy): SVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('width', '20'); svg.setAttribute('height', '20'); svg.setAttribute('aria-hidden', 'true')
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  const d = way === 'email'
    ? 'M3 6h18v12H3zM3 7l9 6 9-6'
    : way === 'text'
      ? 'M4 5h16v11H9l-5 4z'
      : way === 'none'
        ? 'M5 5l14 14M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z'
        : 'M21 11.5a8.5 8.5 0 0 1-12.6 7.4L3 21l2.1-5.4A8.5 8.5 0 1 1 21 11.5z'
  path.setAttribute('d', d)
  path.setAttribute('fill', 'none'); path.setAttribute('stroke', 'currentColor'); path.setAttribute('stroke-width', '2'); path.setAttribute('stroke-linecap', 'round'); path.setAttribute('stroke-linejoin', 'round')
  svg.append(path)
  return svg
}

let dismissBound = false
function bindDismiss(): void {
  if (dismissBound) return
  dismissBound = true
  const closeAll = (keep: Node | null): void => {
    for (const m of document.querySelectorAll<HTMLDetailsElement>('details.way-menu[open]')) if (!keep || !m.contains(keep)) m.open = false
  }
  document.addEventListener('click', (e) => closeAll(e.target as Node))
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAll(null) })
  window.addEventListener('hashchange', () => closeAll(null))
}

// Save this customer's way. `null` = back to the round's usual. Resolves with the saved customer row, or null when
// it did not save (a clash with a change made elsewhere reloads through onConflict).
export async function saveWay(c: WayCustomer, way: CustomerMessageBy | null, onConflict?: () => void): Promise<Record<string, unknown> | null> {
  const ack = await mutate<Record<string, unknown>>({
    label: `How to message ${c.name}`,
    method: 'PATCH',
    path: `/api/${c.route ?? 'customers'}/${c.id}`,
    body: withBase(c, { version: c.version, message_by: way }),
    onConflict: () => { toast(`${c.name} was changed somewhere else. Try again.`); onConflict?.() },
  })
  if (!ack.ok || !ack.row) {
    // Not through yet (no signal): it is kept on this phone and saves when signal returns (the save chip shows
    // it). The screen keeps the old way until then, so it never shows a way that has not saved.
    if (ack.status === 0 || ack.status >= 500) toast('No signal. Saved on this phone to send later.')
    return null
  }
  return ack.row
}

// The ▾ menu on its own, for a place that already has its own send button (or none, for "Doesn't want
// messages"). `changed` gets the saved way (null = the round's usual) once the save is through.
export function wayMenu(c: WayCustomer, changed: (way: CustomerMessageBy | null, row: Record<string, unknown>) => void, opts: { onConflict?: () => void; whatsappPlaced?: () => void } = {}): HTMLDetailsElement {
  const menu = el('details', { class: 'way-menu' })
  // The same drawn arrow as every other Roundly dropdown, not a ▾ letter (Liam, 8 Oct 2026: "slightly off brand").
  const summary = el('summary', { 'aria-label': `How to message ${c.name}`, title: 'Change how to message them' }, el('span', { class: 'way-menu-arrow', 'aria-hidden': 'true' }))
  summary.addEventListener('click', (e) => {
    e.stopPropagation()
    if (!menu.open) for (const other of document.querySelectorAll<HTMLDetailsElement>('details.way-menu[open]')) if (other !== menu) other.open = false
  })
  const list = el('div', { class: 'way-menu-list', role: 'menu' })
  const own = c.message_by ?? null
  const round = roundMessageBy()
  const works = waysThatWork(c)
  list.append(el('div', { class: 'way-menu-head' }, `How to message ${c.name}`))
  const pick = (way: CustomerMessageBy | null) => async (e: Event): Promise<void> => {
    e.stopPropagation()
    menu.open = false
    // Every pick is saved, even one that looks like the current way: a pick made without signal may still be
    // waiting on this phone, and a change of mind has to go after it, not be skipped (Astra, 30 Sep).
    if (c.version === undefined) { toast('Open their page to change this.'); return }
    const was = c.message_by ?? null
    const row = await saveWay(c, way, opts.onConflict)
    if (!row) return
    c.message_by = (row.message_by as string | null | undefined) ?? null
    if (typeof row.version === 'number') c.version = row.version
    // With Undo, as every other change on these screens (bug hunt 8 Oct 2026, messages #12, owed-today #13).
    offerUndo(way === null ? `${c.name}: back to your usual (${wayWords(round).short})` : `${c.name}: ${wayWords(way).short}`, () => {
      void saveWay(c, was as CustomerMessageBy | null, opts.onConflict).then((back) => {
        if (!back) return
        c.message_by = (back.message_by as string | null | undefined) ?? null
        if (typeof back.version === 'number') c.version = back.version
        changed(was as CustomerMessageBy | null, back)
      })
    })
    changed(way, row)
  }
  for (const o of MESSAGE_BY_OPTIONS) {
    const on = own === o.value
    const b = el('button', { type: 'button', role: 'menuitemradio', 'aria-checked': String(on), class: on ? 'way-on' : '' }, wayIcon(o.value), el('span', {}, o.label))
    if (!works.includes(o.value)) b.append(el('span', { class: 'way-menu-note' }, o.value === 'email' ? 'no email yet' : 'no mobile yet'))
    b.addEventListener('click', (e) => void pick(o.value)(e))
    list.append(b)
  }
  // An enquiry asked to hear back: Not interested takes one off instead.
  if (c.route !== 'leads') {
    const none = el('button', { type: 'button', role: 'menuitemradio', 'aria-checked': String(own === 'none'), class: own === 'none' ? 'way-on' : '' }, wayIcon('none'), el('span', {}, "Don't message"))
    none.addEventListener('click', (e) => void pick('none')(e))
    list.append(none)
  }
  // Always offered, so a pick waiting for signal can be taken back to the usual too.
  list.append(el('hr', { class: 'way-menu-rule' }))
  const back = el('button', { type: 'button', role: 'menuitemradio', 'aria-checked': String(own === null), class: own === null ? 'way-on' : '' }, el('span', {}, `Your usual (${wayWords(round).short})`))
  back.addEventListener('click', (e) => void pick(null)(e))
  list.append(back)
  // Computers only: where WhatsApp opens. Kept on this computer, never on the customer.
  if (opts.whatsappPlaced && !onAPhone()) {
    list.append(el('hr', { class: 'way-menu-rule' }), el('div', { class: 'way-menu-head' }, 'WhatsApp on this computer'))
    for (const [place, label] of [['web', 'WhatsApp Web (a browser tab)'], ['app', 'WhatsApp app']] as [WhatsAppPlace, string][]) {
      const on = whatsappPlace() === place
      const b = el('button', { type: 'button', role: 'menuitemradio', 'aria-checked': String(on), class: on ? 'way-on' : '' }, el('span', {}, label))
      b.addEventListener('click', (e) => {
        e.stopPropagation(); menu.open = false
        setWhatsappPlace(place)
        toast(place === 'app' ? 'WhatsApp will open in the app on this computer' : 'WhatsApp will open in WhatsApp Web on this computer')
        opts.whatsappPlaced?.()
      })
      list.append(b)
    }
  }
  menu.append(summary, list)
  menu.addEventListener('toggle', () => {
    if (!menu.open || typeof window === 'undefined') return
    // The room it has: inside the scrolling Messages card on a computer, otherwise the window below the top bar.
    const p2 = !!menu.closest('.enquiry-phone-card')
    const sheet = !!menu.closest('.messages-sheet-card, .messages-sheet-overlay')
    const frame = p2 ? undefined : (menu.closest<HTMLElement>('.messages-sheet-scroll') ?? menu.closest<HTMLElement>('.messages-panel'))?.getBoundingClientRect()
    const bar = document.querySelector<HTMLElement>('.topbar')?.getBoundingClientRect().bottom ?? 0
    const viewport = p2 ? window.visualViewport : null
    const visibleTop = viewport?.offsetTop ?? 0, visibleBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight
    const top = Math.max(frame?.top ?? 0, bar, visibleTop), bottom = Math.min(frame?.bottom ?? visibleBottom, visibleBottom)
    const r = summary.getBoundingClientRect()
    const below = bottom - r.bottom - 12, above = r.top - top - 12
    const up = below < Math.min(list.scrollHeight, 380) && above > below
    menu.dataset.upwards = String(up)
    list.style.maxHeight = `${Math.max(p2 || sheet ? 44 : 160, Math.min(380, up ? above : below))}px`
    // Never off the side of the screen (Liam's iPhone, 13 Oct 2026: "when opend goes of the screen on mobile"): moved
    // back in to 8 points from either edge.
    list.style.translate = ''
    const box = list.getBoundingClientRect()
    const shift = box.left < 8 ? 8 - box.left : box.right > window.innerWidth - 8 ? window.innerWidth - 8 - box.right : 0
    if (shift) list.style.translate = `${Math.round(shift)}px 0`
  })
  bindDismiss()
  return menu
}

export interface SendControl {
  root: HTMLElement
  // Own-app sends on a computer wait for Yes, it sent unless the owner switched the ask off.
  link: HTMLAnchorElement | null
  send: SendLink
}

// The send link + ▾. `label` overrides the button words ("Second chase"); the way's own words are the default.
// `onSend` runs on a phone tap or the computer confirmation. `manualSend` leaves binding to the caller.
export function sendControl(
  c: WayCustomer,
  body: string,
  subject: string,
  opts: { label?: (way: CustomerMessageBy) => string; className?: string; onSend?: (way: CustomerMessageBy) => void; manualSend?: boolean; changed: () => void; onConflict?: () => void },
): SendControl {
  const way = wayFor(c)
  const send = linkFor(way, c, body, subject)
  const root = el('span', { class: 'send-way' })
  const menu = wayMenu(c, () => opts.changed(), { onConflict: opts.onConflict, whatsappPlaced: () => opts.changed() })
  let link: HTMLAnchorElement | null = null
  if (send.href) {
    link = el('a', {
      href: send.href,
      target: way === 'whatsapp' ? whatsappTarget(send.href) : '_self',
      rel: 'noopener',
      class: `${opts.className ?? 'btn-send'} send-${way}`,
      'aria-label': `${wayWords(way).button}: ${c.name}`,
    })
    link.append(wayIcon(way), el('span', {}, opts.label ? opts.label(way) : wayWords(way).button))
    const sent = (e: MouseEvent): void => { if (isSendPress(e)) opts.onSend?.(way) }
    if (!opts.manualSend) {
      const email = link
      bindEmailSend(email, () => ({ href: email.getAttribute('href') ?? '', confirm: !!opts.onSend, sent: () => opts.onSend?.(way) }))
    } else {
      link.addEventListener('click', sent)
      link.addEventListener('auxclick', sent)
    }
    // Right-click on a computer: the menu, not the browser's own.
    link.addEventListener('contextmenu', (e) => {
      e.preventDefault()
      e.stopPropagation()
      for (const other of document.querySelectorAll<HTMLDetailsElement>('details.way-menu[open]')) if (other !== menu) other.open = false
      menu.open = true
    })
    root.append(link)
  } else {
    root.append(el('span', { class: 'send-missing muted' }, wayIcon(way), el('span', {}, send.missing ?? '')))
    // Only when the box is empty: a landline already on file is never overwritten from here.
    const field = way === 'email' ? 'email' : 'phone'
    if (way !== 'none' && c.version !== undefined && !String((c as unknown as Record<string, unknown>)[field] ?? '').trim()) root.append(addContact(c, field, () => opts.changed()))
  }
  root.append(menu)
  return { root, link, send }
}

// "No WhatsApp number on file" with a way to fix it there and then (Liam, 8 Oct 2026: "we have a whatsapp number
// we could upload"). Saves the number (or email) on the customer, with Undo; the caller redraws the send button.
function addContact(c: WayCustomer, field: 'phone' | 'email', redraw: () => void): HTMLElement {
  const wrap = el('span', { class: 'send-add' })
  const open = el('button', { type: 'button', class: 'link-button send-add-open' }, field === 'email' ? 'Add email' : 'Add number')
  const form = el('span', { class: 'send-add-form' }); form.hidden = true
  const input = el('input', { type: field === 'email' ? 'email' : 'tel', autocomplete: 'off', 'aria-label': field === 'email' ? `Email for ${c.name}` : `Mobile or WhatsApp number for ${c.name}`, placeholder: field === 'email' ? 'name@example.com' : callingCode() === '1' ? '(512) 555-0142' : '07700 900123' }) as HTMLInputElement
  const save = el('button', { type: 'button', class: 'btn-primary' }, 'Save')
  const cancel = el('button', { type: 'button', class: 'link-button' }, 'Cancel')
  open.addEventListener('click', (e) => { e.stopPropagation(); open.hidden = true; form.hidden = false; input.focus() })
  cancel.addEventListener('click', (e) => { e.stopPropagation(); input.value = ''; form.hidden = true; open.hidden = false })
  const submit = async (): Promise<void> => {
    const value = input.value.trim()
    if (!value) { input.focus(); return }
    save.disabled = true
    const before = c.version!
    const ack = await mutate<Record<string, unknown>>({
      label: `${field === 'email' ? 'Email' : 'Number'} for ${c.name}`, method: 'PATCH', path: `/api/customers/${c.id}`,
      // Offered only with no number (or email) on the card: made on an empty box (same-time saves).
      body: { version: before, [field]: value, base: { [field]: (c as unknown as Record<string, unknown>)[field] ?? null } },
      onConflict: () => { toast(`${c.name} was changed somewhere else. Try again.`); redraw() },
    })
    save.disabled = false
    if (!ack.ok || !ack.row) { if (ack.status === 0 || ack.status >= 500) toast('No signal. Saved on this phone to send later.'); else if (ack.status !== 409) toast(ack.error ?? 'Could not save that.'); return }
    const row = ack.row
    ;(c as unknown as Record<string, unknown>)[field] = row[field] ?? value
    if (typeof row.version === 'number') c.version = row.version
    undoPatch({ text: `${field === 'email' ? 'Email' : 'Number'} added for ${c.name}`, label: `Undo ${field} for ${c.name}`, path: `/api/customers/${c.id}`, ack, version: before, body: { [field]: null }, after: redraw })
    redraw()
  }
  save.addEventListener('click', (e) => { e.stopPropagation(); void submit() })
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); void submit() } })
  form.append(input, save, cancel)
  wrap.append(open, form)
  return wrap
}
